import { format, stringIsNullOrEmpty, sanitizeAndFormatString } from '../../shared/utils/string.util';
import { IModel, IAttribute, IViolation, IValidationContext } from '../base/annotation';
import { Metamodel } from '../base/model';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { AzureDataExplorerLinkedService } from '../linkedservice/azure-data-explorer.linkedservice';
import { KustoDatabaseLinkedService } from '../linkedservice/kusto-database.linkedservice';
import { LinkedService } from '../linkedservice/linkedservice';
import { ModelConstants } from '../model-constants';
import { Activity } from './activity';
import { validateLinkedServicePermission } from '../../shared/utils/linkedservice-validation-util';
import { Copier } from '../base/copier';
import { GlobalVariables } from '../../constants/globals';

@Metamodel.model({
    name: ModelConstants.kqlActivity,
    display: [
        format(shellStringResources.ModelName_ActivityTemplate, shellStringResources.Kql_DisplayText),
        format(shellStringResources.ModelName_ActivitiesTemplate, shellStringResources.Kql_DisplayText)
    ]
})
export class KqlActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly DATABASE: IAttribute;
    public static readonly COMMAND: IAttribute;
    public static readonly COMMAND_TIMEOUT: IAttribute;

    @Metamodel.attribute({
        display: shellStringResources.Database,
        constraint: {
            validator: KqlActivity.validateDatabase
        },
        association: 'DATABASE',
        accepts: ['String'],
        templatable: true
    })
    public database?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Command,
        constraint: {
            required: true
        },
        association: 'COMMAND',
        accepts: ['String'],
        templatable: true
    })
    public command?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_CommandTimeout,
        association: 'COMMAND_TIMEOUT',
        constraint: {
            pattern: new RegExp('^(\\d+\\.)?((0?\\d)|(1\\d)|(2[0-3]))(:[0-5]?\\d){2}$')
        },
        accepts: ['String'],
        templatable: true
    })
    public commandTimeout?: string = '00:20:00';

    @Metamodel.attribute({
        display: shellStringResources.ConnectionLabel,
        constraint: {
            required: true,
            validator: KqlActivity.validateLinkedService
        },
        reference: true,
        type: LinkedService.MODEL
    })
    public linkedService?: AzureDataExplorerLinkedService | KustoDatabaseLinkedService;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.activityType = 'ExecutionActivity';
    }

    private static validateDatabase(activity: KqlActivity): IViolation[] | undefined {
        if (
            activity && activity.linkedService &&
            stringIsNullOrEmpty(activity.database) &&
            !(activity.linkedService instanceof KustoDatabaseLinkedService)
        ) {
            return [
                {
                    message: () =>
                        sanitizeAndFormatString(
                            shellStringResources.RequiredTemplate,
                            shellStringResources.Database
                        )
                }
            ];
        }
        return undefined;
    }

    private static validateLinkedService(activity: KqlActivity, ls: LinkedService, violation?: IViolation, context?: IValidationContext): IViolation[] {
        const result: IViolation[] = [];
        if (activity.linkedService) {
            result.push(...validateLinkedServicePermission(activity.linkedService, context));
        }
        return result;
    }

    public onActivityCloned(clonedActivity: KqlActivity, context: { inlineDataset?: boolean }): void {
        if (GlobalVariables.isTrident) {
            if (this.linkedService?.parameterized) {
                clonedActivity.linkedService = Copier.clone(
                    context,
                    [this.linkedService],
                    undefined,
                    true
                )[0] as LinkedService;
            }
        }
    }
}
